import Details from '@/components/Details/Details';
import TimeZoneSelector from '@/components/TimeZoneSelect/time-zone-select.component';
import { ICalendar } from '@/types/ICalendar';
import {
	Box,
	Button,
	Select as CSelect,
	FormControl,
	FormLabel,
	Input,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
} from '@chakra-ui/react';
import { Select } from 'chakra-react-select';
import { t } from 'i18next';
import { useEditCalendarController } from './edit-calendar-modal.controller';

interface IProps {
	onClose: () => void;
	calendar: ICalendar;
	isOpen: boolean;
}

export function EditCalendarModal(props: IProps) {
	const {
		eventModeOption,
		form,
		isLoading,
		options,
		selectedOption,
		borderColorBehavior,
		pendingEventOption,
		statusWhenDatesChanged,
		handleBorderColorBehaviorChange,
		handlePendingEventOptionChange,
		handleSubmit,
		handleEventModeChange,
		handleStatusWhenDatesChangedChange,
	} = useEditCalendarController(props);
	return (
		<Modal isOpen={props.isOpen} onClose={props.onClose} size="xl">
			<ModalOverlay />

			<ModalContent>
				<ModalHeader>{t('Edit calendar')}</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<Box id="edit-calendar-form" as="form" onSubmit={handleSubmit}>
						<Details title={t('General options')}>
							<FormControl isDisabled={isLoading}>
								<FormLabel>{t('Name')}</FormLabel>
								<Input {...form.register('name')} type="text" />
							</FormControl>

							<FormControl isDisabled={isLoading} mt={4}>
								<FormLabel>{t('Description')}</FormLabel>
								<Input {...form.register('description')} type="text" />
							</FormControl>

							<FormControl isDisabled={isLoading} mt={4}>
								<FormLabel>{t('TimeZone')}</FormLabel>
								<TimeZoneSelector
									value={form.watch('defaultTimeZone') || ''}
									onChange={(v) => {
										form.setValue('defaultTimeZone', v);
									}}
								/>
							</FormControl>

							<FormControl isDisabled={isLoading} data-testid="owner-select" mt={4}>
								<FormLabel htmlFor="owner">{t('Owner')}</FormLabel>
								<Select
									inputId="owner"
									value={selectedOption}
									getOptionLabel={(org) => org.name}
									getOptionValue={(org) => org.id}
									options={options}
									onChange={(v) => {
										if (!v) {
											return;
										}

										form.setValue('owner', v);
									}}
								/>
							</FormControl>
						</Details>

						<Details title={t('Advanced options')}>
							<FormControl isDisabled={isLoading} mt={4}>
								<FormLabel>{t('How events are created')}</FormLabel>

								<CSelect value={eventModeOption} onChange={handleEventModeChange}>
									<option value="SAVE">{t('Create new events as confirmed')}</option>
									<option value="DRAFT">{t('Create new events as pending')}</option>
								</CSelect>
							</FormControl>

							<FormControl isDisabled={isLoading} mt={4}>
								<FormLabel>{t('Pending events')}</FormLabel>
								<CSelect value={pendingEventOption} onChange={handlePendingEventOptionChange}>
									<option value="">{t('Do not display a dot on tentative events.')}</option>
									<option value="DOT">{t('Display a dot on tentative events.')}</option>
								</CSelect>
							</FormControl>

							<FormControl isDisabled={isLoading} mt={4}>
								<FormLabel>{t('Event border color behavior')}</FormLabel>
								<CSelect value={borderColorBehavior} onChange={handleBorderColorBehaviorChange}>
									<option value="EVENT_STATUS">{t('Change the color based on event status')}</option>
									<option value="ATTENDEE_STATUS">{t('Change the color based on attendee responses')}</option>
								</CSelect>
							</FormControl>

							<FormControl isDisabled={isLoading} mt={4}>
								<FormLabel>{t('When the dates of an event are changed')}</FormLabel>
								<CSelect value={statusWhenDatesChanged} onChange={handleStatusWhenDatesChangedChange}>
									<option value="KEEP">{t('Keep the current event status')}</option>
									<option value="SET_TENTATIVE">{t('Change the status to Tentative')}</option>
								</CSelect>
							</FormControl>
						</Details>
					</Box>
				</ModalBody>
				<ModalFooter>
					<Button isLoading={isLoading} type="submit" form="edit-calendar-form" colorScheme="primary">
						{t('Save')}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
}
